"use client";
import React, { useState, useRef, useCallback } from "react";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Users,
  FileUp,
  Trash2,
} from "lucide-react";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import { apiFetch } from "@/service/api";

interface UploadResponse {
  total: number;
  creados: number;
  errores: number;
  detalleErrores?: Array<{ fila: number; error: string }>;
}

export default function UsuariosBulkUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<UploadResponse | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    setFile(e.target.files[0]);
    setResponse(null);
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (
      droppedFile &&
      (droppedFile.name.endsWith(".xlsx") || droppedFile.name.endsWith(".xls"))
    ) {
      setFile(droppedFile);
      setResponse(null);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleUpload = async () => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      setLoading(true);
      const res = await apiFetch(`usuarios/bulk-upload`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      setResponse(data);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (error) {
      console.error(error);
      setResponse({
        total: 0,
        creados: 0,
        errores: 1,
        detalleErrores: [
          { fila: 0, error: "Error al conectar con el servidor" },
        ],
      });
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } finally {
      setLoading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setResponse(null);
    if (inputRef.current) inputRef.current.value = "";
  };
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };
  const successRate = response
    ? Math.round((response.creados / response.total) * 100) || 0
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 py-12 px-4">
      <div className="mx-auto max-w-3xl space-y-8">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center rounded-full bg-primary/10 p-3 mb-4">
            <Users className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Carga Masiva de Usuarios
          </h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            Importe multiples usuarios a la vez mediante un archivo Excel
          </p>
        </div>
        <Card className="border-dashed">
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Descargue el archivo de plantilla requerido:
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="/Plantilla.xlsx"
                download="Plantilla.xlsx"
                className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted transition"
              >
                <FileSpreadsheet className="h-6 w-6 text-green-600" />
                <div className="text-left">
                  <p className="text-sm font-medium">Plantilla Excel</p>
                  <p className="text-xs text-muted-foreground">
                    Descargar formato requerido
                  </p>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-6">
            <div>
              <h4 className="font-medium mb-3">Columnas requeridas</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div className="rounded-lg border p-3">
                  <p className="font-medium">A</p>
                  <p className="text-muted-foreground">Numero de documento</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="font-medium">B</p>
                  <p className="text-muted-foreground">Nombres</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="font-medium">C</p>
                  <p className="text-muted-foreground">Apellidos</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="font-medium">D</p>
                  <p className="text-muted-foreground">Correo electronico</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="font-medium">E</p>
                  <p className="text-muted-foreground">Telefono</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="font-medium">F</p>
                  <p className="text-muted-foreground">Rol</p>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">Roles permitidos</h4>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-md bg-green-100 text-green-700 text-sm">
                  Administrador
                </span>
                <span className="px-3 py-1 rounded-md bg-blue-100 text-blue-700 text-sm">
                  Colaborador
                </span>
                <span className="px-3 py-1 rounded-md bg-purple-100 text-purple-700 text-sm">
                  Supervisor
                </span>
                <span className="px-3 py-1 rounded-md bg-yellow-100 text-yellow-700 text-sm">
                  Asistente
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                El rol debe escribirse exactamente igual en el archivo Excel eso
                incluye mayusculas y minusculas.
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <b>Subir archivo</b>
            <p className="text-sm text-muted-foreground">
              Arrastre y suelte un archivo Excel o haga clic para seleccionar
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => inputRef.current?.click()}
              className={`relative cursor-pointer rounded-xl border-2 border-dashed p-10 transition-all duration-200 ease-in-out ${
                isDragging
                  ? "border-primary bg-primary/5 scale-[1.02]"
                  : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50"
              } ${file ? "border-primary/50 bg-primary/5" : ""}`}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="flex flex-col items-center justify-center gap-4 text-center">
                {file ? (
                  <>
                    <div className="rounded-full bg-primary/10 p-4">
                      <FileSpreadsheet className="h-8 w-8 text-primary" />
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium text-foreground">{file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rounded-full bg-muted p-4">
                      <Upload className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium text-foreground">
                        Arrastre su archivo aqui
                      </p>
                      <p className="text-sm text-muted-foreground">
                        o haga clic para seleccionar (.xlsx,.xls)
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
            {file && (
              <div className="flex items-center justify-between gap-4">
                <Button
                  onClick={(e: any) => {
                    e.stopPropagation();
                    clearFile();
                  }}
                  className="gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Eliminar archivo
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={loading}
                  className="gap-2 min-w-"
                >
                  {loading ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <FileUp className="h-4 w-4" />
                      Subir archivo
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        {response && (
          <Card>
            <CardHeader>
              <b className="flex items-center gap-2">
                {response.errores === 0 ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                ) : response.creados === 0 ? (
                  <XCircle className="h-5 w-5 text-destructive" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                )}
                Resultado de la importacion
              </b>
              <p className="text-sm text-muted-foreground">
                Resumen del procesamiento del archivo
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-lg bg-blue-500/10 p-4 text-center">
                  <p className="text-2xl font-semibold text-blue-600">
                    {response.total}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Total registros
                  </p>
                </div>
                <div className="rounded-lg bg-emerald-500/10 p-4 text-center">
                  <p className="text-2xl font-semibold text-emerald-600">
                    {response.creados}
                  </p>
                  <p className="text-sm text-muted-foreground">Creados</p>
                </div>
                <div className="rounded-lg bg-red-500/10 p-4 text-center">
                  <p className="text-2xl font-semibold text-red-600">
                    {response.errores}
                  </p>
                  <p className="text-sm text-muted-foreground">Errores</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Tasa de exito</span>
                  <span className="font-medium text-foreground">
                    {successRate}%
                  </span>
                </div>
              </div>
              {response.detalleErrores &&
                response.detalleErrores.length > 0 && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-destructive" />
                      Detalle de errores
                    </h4>
                    <div className="rounded-lg border overflow-hidden">
                      <Table>
                        <TableBody>
                          {response.detalleErrores.map((err, index) => (
                            <TableRow key={index}>
                              <TableCell className="text-muted-foreground px-8">
                                <div className="flex items-center gap-2">
                                  <AlertCircle className="h-4 w-4 text-red-500" />
                                  <span>{err.error}</span>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
